/**
 * PractComUpgradePage — public thank-you / upgrade landing page that the
 * 14-day Smile Design AI → PractCom upsell email links to.
 *
 *   /practcom-upgrade?ref=smile_upsell_d14&name=Sarah+Chen
 *
 * The email passes the recipient's name via ?name= so the page can greet
 * them personally as "Hi Dr. {LastName},". `ref` is preserved through to
 * the SamCart checkout URL for attribution.
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, CheckCircle2, ArrowRight, Loader2,
  Stethoscope, FileSignature, Heart, Sparkles,
  MessageSquare, Smartphone, Globe, ShieldAlert, Users,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: Stethoscope,   title: 'Post-Op & Med Cards',
    body: 'Clear patient guidance sent consistently after every treatment.' },
  { icon: FileSignature, title: 'Informed Consents',
    body: 'Professional communication and documentation in one place.' },
  { icon: Heart,         title: 'Referrals & Thank-Yous',
    body: 'Track referral relationships and automate appreciation.' },
  { icon: Sparkles,      title: 'Smile Design AI — included',
    body: 'Help patients visualize treatment and say yes faster.' },
  { icon: MessageSquare, title: 'Texting & Analytics',
    body: 'Text patients directly and track communication performance.' },
  { icon: Smartphone,    title: 'Patient Portal', badge: 'NEW',
    body: 'Patients access their aftercare documents anytime, anywhere.' },
  { icon: Globe,         title: '15 Languages', badge: 'NEW',
    body: 'Deliver instructions in 15 languages for every patient.' },
  { icon: ShieldAlert,   title: 'Compliance Mgmt', badge: 'NEW',
    body: 'Audit trails and standardized workflows to reduce liability.' },
  { icon: Users,         title: 'Hire Staff', badge: 'NEW',
    body: 'One-click access to all major job boards + an exclusive private dental talent marketplace — at no extra cost.' },
];

const STATS = [
  { num: '100+',  label: 'Ready-to-use templates' },
  { num: '< 2 min', label: 'Setup time' },
  { num: '1 system', label: 'Communication workflows' },
];

// --- Local helper: extract "Dr. LastName" from a free-form name -----------
function drLastName(raw) {
  const txt = (raw || '').trim();
  if (!txt) return null;
  const parts = txt.replace(/,/g, ' ').split(/\s+/).filter(Boolean);
  const titles = new Set(['dr', 'dr.', 'doctor', 'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.']);
  while (parts.length && titles.has(parts[0].toLowerCase())) parts.shift();
  const suffixes = new Set([
    'dds','d.d.s.','d.d.s','dmd','d.m.d.','d.m.d',
    'md','m.d.','m.d','phd','p.h.d.','p.h.d',
    'jr','jr.','sr','sr.'
  ]);
  while (parts.length && suffixes.has(parts[parts.length-1].toLowerCase().replace(/,/g,''))) parts.pop();
  if (!parts.length) return null;
  const last = parts[parts.length - 1];
  return 'Dr. ' + (last.length > 1 ? last[0].toUpperCase() + last.slice(1).toLowerCase() : last.toUpperCase());
}

export default function PractComUpgradePage() {
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState(null);

  const qp        = new URLSearchParams(window.location.search);
  const refTag    = qp.get('ref') || '';
  const visitor   = qp.get('name') || '';
  const greeting  = drLastName(visitor); // null if no name was passed

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/public/practcom-checkout-url`);
        if (!res.ok) throw new Error(`Could not load checkout URL (${res.status})`);
        const data = await res.json();
        let url = data.url || '';
        if (url && refTag) {
          const sep = url.includes('?') ? '&' : '?';
          url = `${url}${sep}ref=${encodeURIComponent(refTag)}`;
        }
        setCheckoutUrl(url);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refTag]);

  return (
    <div data-testid="practcom-upgrade-page"
         className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-white">
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center font-extrabold">
              P
            </div>
            <div className="leading-tight">
              <p className="font-extrabold text-slate-900">PractCom</p>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">by Cary H. Ganz, DDS</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Secure SamCart checkout</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-12 pb-8">
        {greeting && (
          <p className="text-lg sm:text-xl font-extrabold text-blue-700 mb-3"
             data-testid="visitor-greeting">
            Hi {greeting},
          </p>
        )}
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700 mb-3">
          Thank you for your trust
        </p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">
          You're already using Smile Design AI.
          <br className="hidden sm:block" />
          Here's how to add the rest of PractCom.
        </h1>
        <p className="text-base text-slate-600 mt-5 max-w-2xl leading-relaxed">
          Smile Design AI is included for free inside PractCom — the full
          practice-communication suite I built for my own dental office over the
          last 30 years. When you upgrade below, your Smile Design AI access
          continues without interruption, your standalone billing is cancelled
          automatically, and you only ever see <em>one</em> charge.
        </p>
      </section>

      {/* CTA card (with pricing) */}
      <section className="max-w-3xl mx-auto px-6">
        <div className="bg-white border-2 border-blue-200 rounded-2xl shadow-sm p-7"
             data-testid="upgrade-cta-card">
          {/* Price strip */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-5 pb-5 border-b border-slate-100">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Entire suite — pick your plan</p>
              <p className="mt-3 text-lg text-slate-800 font-semibold">
                Essential · Professional · Enterprise
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> 15-day free trial</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Cancel anytime</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> No credit card to start</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-lg font-extrabold text-slate-900">Upgrade to PractCom</p>
              <p className="text-sm text-slate-600 mt-1">
                Everything you have stays — plus the rest of the suite. About 60 seconds.
              </p>
            </div>
            {loading ? (
              <button disabled
                      className="inline-flex items-center gap-2 bg-slate-300 text-white px-6 py-3 rounded-xl font-bold text-sm"
                      data-testid="upgrade-cta-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading checkout…
              </button>
            ) : checkoutUrl ? (
              <a href={checkoutUrl}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all"
                 data-testid="upgrade-cta-button">
                Continue to secure checkout <ArrowRight className="w-4 h-4" />
              </a>
            ) : (
              <button
                onClick={() => (window.location.href = '/plans-billing')}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all"
                data-testid="upgrade-cta-button-plans"
              >
                See all plans <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-slate-600">
            <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Standalone Smile Design AI billing cancelled automatically</span></div>
            <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Everything you created in Smile Design AI ports over — patients, logos, transformations, links</span></div>
            <div className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" /><span>Reply to the email anytime — Cary reads every one</span></div>
          </div>
        </div>
      </section>

      {/* Will my data port over? — explicit reassurance block */}
      <section className="max-w-3xl mx-auto px-6 mt-10">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5"
             data-testid="data-portability-card">
          <p className="text-sm font-extrabold text-emerald-900">Will what I created in the standalone app port over?</p>
          <p className="text-sm text-emerald-900/80 mt-1 leading-relaxed">
            Yes — every patient, logo, transformation, QR code, and shareable
            link you built in standalone Smile Design AI carries over to your
            new PractCom account automatically. Same login email. No re-uploads,
            no re-onboarding. Your existing patient-facing links keep working
            exactly as they do today.
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="max-w-3xl mx-auto px-6 mt-12">
        <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-2">
          Everything Your Practice Needs in <span className="text-blue-600">One Platform</span>
        </h2>
        <p className="text-sm text-slate-600 mb-6">
          From the chair to the patient's phone — PractCom handles the full communication loop.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {FEATURES.map(({ icon: Icon, title, body, badge }) => (
            <div key={title}
                 className="bg-white border border-slate-200 rounded-xl p-4 flex gap-3"
                 data-testid={`feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 grid place-items-center flex-shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
                  {title}
                  {badge && (
                    <span className="inline-block bg-emerald-500 text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-full uppercase">
                      {badge}
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          {STATS.map(s => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 text-center"
                 data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <p className="text-xl sm:text-2xl font-extrabold text-blue-600">{s.num}</p>
              <p className="text-[11px] sm:text-xs text-slate-600 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Keep what you have */}
      <section className="max-w-3xl mx-auto px-6 mt-12">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5"
             data-testid="keep-standalone-card">
          <p className="text-sm font-extrabold text-amber-900">Prefer to keep Smile Design AI standalone?</p>
          <p className="text-sm text-amber-900/80 mt-1 leading-relaxed">
            That's perfectly fine — no action needed. Your standalone subscription
            continues exactly as it is today. This page is the only nudge you'll receive.
          </p>
          <a href="https://app.smiledesignai.org/"
             className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-amber-900 hover:text-amber-700"
             data-testid="back-to-smile-link">
            Back to Smile Design AI <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* Signature */}
      <section className="max-w-3xl mx-auto px-6 mt-12">
        <div className="bg-white border border-slate-200 rounded-xl p-6"
             data-testid="signature-card">
          <p className="text-sm text-slate-700 leading-relaxed">
            Thanks for your continued trust,
          </p>
          <p className="mt-3 text-base font-extrabold text-slate-900">Cary H. Ganz, DDS</p>
          <p className="text-xs text-slate-600">Creator of PractCom and Smile Design AI</p>
          <p className="text-xs text-slate-600 mt-2">
            <a href="mailto:admin@practcom.com"
               className="text-blue-700 font-semibold hover:underline"
               data-testid="signature-email-link">admin@practcom.com</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="max-w-3xl mx-auto px-6 mt-10 pb-12 text-center text-xs text-slate-500">
        <p>PractCom · Saint James, NY · <a href="https://app.practcom.com/"
                                            className="underline hover:text-slate-700">app.practcom.com</a></p>
        <p className="mt-1">
          Questions? Email{' '}
          <a href="mailto:admin@practcom.com" className="text-blue-700 hover:underline">admin@practcom.com</a> — Cary reads every one personally.
        </p>
        {err && (
          <p className="mt-3 text-rose-600" data-testid="upgrade-page-error">
            ({err})
          </p>
        )}
      </footer>
    </div>
  );
}
